import { supabase } from '../supabase';
import { callGemini } from './providers/gemini';
import { callOpenAI } from './providers/openai';
import { callClaude } from './providers/claude';
import { callMiniMax } from './providers/minimax';
import { callGroq } from './providers/groq';
import { callMistral } from './providers/mistral';
import { callCohere } from './providers/cohere';
import { callDeepSeek } from './providers/deepseek';
import { callTogether } from './providers/together';
import { decryptKey } from './crypto';
import { useStore } from '../../store/useStore';
import { triggerNeuralReflection } from './neural-reflection';
import { toast } from 'react-hot-toast';
import { APIResponse, AIContext } from './types';

const rateLimitCooldowns: Record<string, number> = {};
export async function callExternalAPI(
  input: string, 
  context: AIContext = {},
  signal?: AbortSignal
): Promise<APIResponse> {
  console.log('[API Router] callExternalAPI called. Input length:', input.length, 'Source:', context.source);
  try {
    const state = useStore.getState();
    const initialProvider = context.provider || state.preferred_api_provider || 'gemini';

    console.log('[API Router] Intercept check - provider:', initialProvider);

    // HANDLE LOCAL PROVIDER IMMEDIATELY (Bypass cloud loop)
    if (initialProvider === 'local' || initialProvider === 'os-bot') {
      console.log('[API Router] Routing to Local OS Brain');
      try {
        const { recognizeIntent, TaskExecutor, generateResponse } = await import('../local-ai');
        const nlu = await recognizeIntent(input);
        
        if (!nlu) {
          console.log('[API Router] Local OS Brain: No intent recognized');
          return {
            response: "I'm sorry, I couldn't understand that command locally. Try asking for 'help' to see what I can do!",
            model: 'local',
            provider: 'local'
          };
        }

        const execution = await TaskExecutor.execute(nlu.intent.name, nlu.params);
        const response = generateResponse(nlu.intent, execution, input, state.aiTone || 'Professional');
        
        // Update usage tracking (local usage is non-premium)
        state.incrementAiUsage(1, false);
        
        return {
          response: response,
          model: 'local',
          provider: 'local'
        };
      } catch (err) {
        console.error('[API Router] Local OS Brain failure:', err);
        return {
          response: "Local AI error: " + (err instanceof Error ? err.message : String(err)),
          model: 'local',
          provider: 'local'
        };
      }
    }

    // --- CONTEXT PREPARATION ---
    const leads = useStore.getState().leads || [];
    const tasks = useStore.getState().tasks || [];

    const pendingTasks = tasks.filter(t => t.status !== 'done');
    const userAlias = useStore.getState().aiUserAlias || 'User';
    const aiMemories = useStore.getState().aiMemories || [];
    const memoriesText = aiMemories.length > 0 
      ? `\nCustom Memories / Facts about User:\n${aiMemories.map((m) => `- ${m}`).join('\n')}`
      : '';

    const customPrompt = useStore.getState().aiCustomPrompt || '';
    const customPromptText = customPrompt.trim().length > 0
      ? `\nCRITICAL CUSTOM INSTRUCTIONS (You MUST strictly follow these rules/personality/instructions in your response): ${customPrompt}\n`
      : '';

    const systemPrompt = `You are OS Bot, a real estate CRM assistant.
Answer in a way that feels premium and intelligent.
${memoriesText}
${customPromptText}

If you need to perform an action (like creating a task, updating a lead, or sending an SMS), respond with a JSON object in this format:
{
  "intent": "action_name",
  "response": "Your conversational response to the user",
  "data": { "param1": "value1" }
}

If the user asks for "high equity", set "min_equity" to 50 in the search_leads data.

Available Actions:
- add_task { description: string, dueDate?: string }
- update_status { leadName: string, status: string }
- send_sms { number: string, message: string }
- create_lead { name: string }
- navigate { path: string }
- list_leads {}
- list_tasks {}
- search_leads { city?: string, state?: string, min_equity?: number, query?: string }
- create_automation { name: string, trigger: { type: string, config?: object }, actions: Array<{ type: string, config?: object }> }

Current State Context:
- Active Leads: ${leads.slice(0, 10).map(l => `${l.name} (ID: ${l.id}, Status: ${l.status})`).join(', ')}
- Pending Tasks: ${pendingTasks.slice(0, 10).map(t => `${t.title} (ID: ${t.id})`).join(', ')}
- Tone: ${useStore.getState().aiTone || 'Professional'}
- User Name: ${userAlias}`;

    // Ensure context has the systemPrompt for providers
    const enrichedContext = { ...context, systemPrompt };


    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user found');

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_api_keys, preferred_api_provider, api_fallback_enabled, credits_remaining, smart_rotate_enabled')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.warn('[API Router] Profile fetch failed, using defaults:', profileError);
    }
    
    // --- PROVIDER RESOLUTION ---
    // Prioritize context.provider or context.modelId over the saved profile setting
    let provider = context.provider || context.modelId || profile?.preferred_api_provider || 'gemini';
    
    // Normalize provider: mapping specific model names to their provider IDs
    if (provider.includes('gpt')) provider = 'openai';
    if (provider.includes('claude')) provider = 'claude';
    if (provider.includes('gemini')) provider = 'gemini';
    if (provider.includes('deepseek')) provider = 'deepseek';
    if (provider.includes('mistral')) provider = 'mistral';
    if (provider.includes('groq')) provider = 'groq';
    if (provider.includes('together')) provider = 'together';
    if (provider.includes('minimax')) provider = 'minimax';
    if (provider.includes('cohere')) provider = 'cohere';

    console.log(`[API Router] Resolved Provider: ${provider}`);

    // Merge keys from the user_api_keys JSON column
    const apiKeysData: Record<string, any> = {
      ...(profile?.user_api_keys || {})
    };
    
    // Support for testing specific keys directly from settings
    if (context.testKey) {
      console.log(`[API Router] Using provided testKey override for ${provider}`);
      apiKeysData[provider] = context.testKey;
    }

    const hasBYOKey = !!apiKeysData[provider];
    console.log(`[API Router] Key found: ${hasBYOKey}`);

    // Determine the list of providers to try
    let providersToTry: string[] = [provider];
    
    if (profile?.api_fallback_enabled && !context.testKey && !context.disableFallback) {
      const fallbackOrder = profile?.api_fallback_order || ['gemini', 'openai', 'claude', 'local'];
      
      // Filter out providers the user doesn't have keys/credits for (except initial and local)
      const validFallbacks = fallbackOrder.filter((id: string) => {
        if (id === provider || id === 'local') return true;
        const hasKey = !!apiKeysData[id];
        const hasPlatformKey = !!(import.meta as any).env[`VITE_${id.toUpperCase()}_API_KEY`] && (profile?.credits_remaining || 0) > 0;
        return hasKey || hasPlatformKey;
      });

      // Combine: start with current provider, then follow the user's fallback order (skipping the current one)
      const others = validFallbacks.filter((id: string) => id !== provider);
      providersToTry = [...providersToTry, ...others];
    }
    
    // Always ensure local is the absolute final fallback
    if (!providersToTry.includes('local') && !context.disableFallback) {
      providersToTry.push('local');
    }

    // Apply rate limit cooldowns (skip cooldown if it is a specific test)
    if (!context.testKey) {
      providersToTry = providersToTry.filter(prov => {
        if (prov === 'local') return true;
        const cooldownEnd = rateLimitCooldowns[prov];
        if (cooldownEnd && Date.now() < cooldownEnd) {
          console.warn(`[API Router] Skipping ${prov} - on rate limit cooldown until ${new Date(cooldownEnd).toLocaleTimeString()}`);
          return false;
        }
        return true;
      });
    }

    if (providersToTry.length === 0) providersToTry.push('local');

    console.log('[API Router] Full Fallback Chain:', providersToTry.join(' -> '));

    interface RouterError {
      name?: string;
      message?: string | Record<string, unknown> | unknown;
      stack?: string;
      error?: { message?: string; [key: string]: unknown } | string;
      status?: number;
      code?: string | number;
    }

    let lastError: RouterError | null = null;

    for (const currentProvider of providersToTry) {
      try {
        const isInitialProvider = currentProvider === provider;
        
        // Skip if no keys and no credits for initial provider
        if (!apiKeysData[currentProvider] && (profile?.credits_remaining || 0) <= 0) {
          if (isInitialProvider) {
            console.warn(`[API Router] User ${user.id} has no premium credits remaining and no BYO key for ${currentProvider}.`);
            throw new Error(`No premium credits remaining and no personal api key provided for ${currentProvider}`);
          }
          continue;
        }

        let providerKeys = apiKeysData[currentProvider];
        let isPremium = false;
        
        if (!providerKeys) {
          const platformKey = (import.meta as any).env[`VITE_${currentProvider.toUpperCase()}_API_KEY`];
          if (platformKey && (profile?.credits_remaining || 0) > 0) {
            console.log(`[API Router] Using platform key for ${currentProvider}. Credits remaining: ${profile?.credits_remaining}`);
            providerKeys = platformKey;
            isPremium = true;
          } else {
            if (isInitialProvider) {
               throw new Error(`Provider ${currentProvider} requires either a personal API key or a premium balance.`);
            }
            continue;
          }
        }

        const keysArray = Array.isArray(providerKeys) ? providerKeys : [providerKeys];
        const smartRotate = profile?.smart_rotate_enabled && (keysArray.length > 1 || providersToTry.length > 1);

        for (let i = 0; i < keysArray.length; i++) {
          try {
            const currentKey = keysArray[i];
            let apiKey: string;
            
            if (isPremium || context.testKey) {
                apiKey = currentKey;
            } else {
                try {
                    const decrypted = decryptKey(currentKey, user.id);
                    apiKey = (decrypted && decrypted.length > 5) ? decrypted : currentKey;
                } catch (e) {
                    apiKey = currentKey;
                }
            }

            if (!apiKey || apiKey.trim().length < 8) {
                continue;
            }

            console.log(`[API Router] Attempting ${currentProvider} (Key ${i + 1}/${keysArray.length})`);

            // --- MODEL FALLBACK DEFINITIONS ---
            const modelsByProvider: Record<string, string[]> = {
              gemini: ['gemini-2.5-flash', 'gemini-1.5-flash'],
              openai: ['gpt-4o', 'gpt-4o-mini'],
              claude: ['claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'],
              deepseek: ['deepseek-chat'],
              groq: ['llama-3.3-70b-versatile', 'mixtral-8x7b-32768'],
              mistral: ['mistral-tiny', 'mistral-small', 'mistral-medium'],
              together: ['meta-llama/Llama-3.2-3B-Instruct-Turbo', 'meta-llama/Llama-3-70b-chat-hf'],
              minimax: ['abab7-chat'],
              cohere: ['command-r', 'command-r-plus'],
              local: ['local']
            };

            const models = modelsByProvider[currentProvider] || ['default'];
            let modelResponse: string | null = null;
            let modelError: Error | null = null;

            for (let m = 0; m < models.length; m++) {
              const currentModel = models[m];
              (enrichedContext as any).modelOverride = currentModel;
              
              // --- ROUTING ---
              console.log(`[API Router] Request to ${currentProvider} with model ${currentModel}:`, { 
                provider: currentProvider,
                keyLength: apiKey?.length 
              });

              try {
                let response: string | null = null;
              const finalInput = (enrichedContext as any).replyTo 
                ? `[CONTEXT: USER IS REPLYING TO: "${(enrichedContext as any).replyTo.content}"]\n\n${input}` 
                : input;

              switch (currentProvider) {
                case 'openai':
                    response = await callOpenAI(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'claude':
                    response = await callClaude(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'gemini':
                    response = await callGemini(finalInput, apiKey, (enrichedContext as any).systemPrompt, signal, currentModel, (enrichedContext as any).messages);
                    break;
                case 'deepseek':
                    response = await callDeepSeek(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'groq':
                    response = await callGroq(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'mistral':
                    response = await callMistral(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'together':
                    response = await callTogether(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'minimax':
                    response = await callMiniMax(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'cohere':
                    response = await callCohere(finalInput, enrichedContext, apiKey, signal);
                    break;
                case 'local':
                    console.log('[API Router] Fallback: Routing to Local OS Brain');
                    try {
                      const { recognizeIntent, TaskExecutor, generateResponse } = await import('../local-ai');
                      
                      // Shared Memory Retrieval for Local AI
                      let memoryContext = '';
                      if (state.useSharedMemoryForLocalAI) {
                        try {
                          const { data: similar } = await supabase
                            .from('shared_memory')
                            .select('question, response')
                            .textSearch('question', input.split(' ').join(' | '))
                            .limit(3);
                          
                          if (similar && similar.length > 0) {
                            memoryContext = `\nCollective Memory Context:\n${similar.map((s: any) => `Q: ${s.question}\nA: ${s.response}`).join('\n')}\n`;
                            console.log('[API Router] Injected shared memory context into local AI');
                          }
                        } catch (memErr) {
                          console.warn('[API Router] Shared memory retrieval failed:', memErr);
                        }
                      }

                      const nlu = await recognizeIntent(input);
                      if (!nlu) {
                        response = "I'm sorry, I couldn't understand that command locally.";
                      } else {
                        const execution = await TaskExecutor.execute(nlu.intent.name, nlu.params);
                        response = generateResponse(nlu.intent, execution, input, undefined, memoryContext);
                      }
                      state.incrementAiUsage(1, false);
                    } catch (err) {
                      console.error('[API Router] Local fallback failed:', err);
                      throw err;
                    }
                    break;
                default:
                    continue;
              }

              if (response) {
                modelResponse = response;
                break; // Model success, break out of models loop
              }
            } catch (err) {
              modelError = err as Error;
              console.log(`[API Router] Model ${currentModel} failed. Trying next model...`);
            }
          } // end models loop

          if (!modelResponse) {
             throw modelError || new Error(`All models for ${currentProvider} failed.`);
          }
          
          const response = modelResponse;

          // Trigger reflection only if not a test and not explicitly skipped
          if (!context.testKey && !context.skipReflection) {
              triggerNeuralReflection(input, response, user.id, currentProvider);
          }


                    // Shared Memory Contribution
                    if (state.contributeToSharedMemory && currentProvider !== 'local') {
                      supabase.from('shared_memory').insert({
                        user_id: user.id,
                        question: input,
                        response: response,
                        provider: currentProvider
                      }).then(({ error }: { error: any }) => {
                        if (error) console.warn('[API Router] Shared memory contribution failed:', error);
                        else console.log('[API Router] Contributed to shared memory');
                      });
                    }

                    return {
                        response: response,
                        model: (enrichedContext as any).modelOverride || 'default',
                        provider: currentProvider
                    };
          } catch (error: any) {
            let errorMsg = '';
            if (error) {
              if (typeof error === 'string') {
                errorMsg = error;
              } else if (typeof error.message === 'string') {
                errorMsg = error.message;
              } else if (error.message && typeof error.message === 'object') {
                errorMsg = JSON.stringify(error.message);
              } else if (error.error && typeof error.error === 'string') {
                errorMsg = error.error;
              } else if (error.error?.message && typeof error.error.message === 'string') {
                errorMsg = error.error.message;
              } else {
                errorMsg = JSON.stringify(error);
              }
            }
            const errorMsgLower = errorMsg.toLowerCase();
            const status = error.status || error.code || 500;
            const isRetryable = errorMsgLower.includes('429') || errorMsgLower.includes('rate limit') || errorMsgLower.includes('quota') || errorMsgLower.includes('exhausted') || status === 429;

            lastError = error;

            if (isRetryable) {
              rateLimitCooldowns[currentProvider] = Date.now() + 60000; // 60s cooldown
            }

            if (smartRotate && isRetryable) {
              if (i < keysArray.length - 1) {
                console.warn(`[API Router] Rotating to next key for ${currentProvider}`);
                continue;
              } else if (providersToTry.indexOf(currentProvider) < providersToTry.length - 1) {
                const nextIdx = providersToTry.indexOf(currentProvider) + 1;
                const nextProvider = providersToTry[nextIdx];
                const reason = isRetryable ? "quota exceeded or rate limit" : "unresponsive";
                const msg = `Switched to ${nextProvider.toUpperCase()} because ${currentProvider.toUpperCase()} hit a ${reason}.`;
                console.warn(`[API Router] ${msg}`);
                if (!context.silent) toast(msg, { icon: '🔄', duration: 5000 });
                break; // Break currentProvider keys loop to go to nextProvider
              }
            }
            throw error; // Not retryable or no more options
          }
        }
      } catch (err: any) {
        lastError = err;
        if (!profile?.smart_rotate_enabled) throw err;
      }
    }

    if (lastError) {
      let msg = '';
      if (lastError) {
        if (typeof lastError === 'string') {
          msg = lastError;
        } else if (typeof lastError.message === 'string') {
          msg = lastError.message;
        } else if (lastError.message && typeof lastError.message === 'object') {
          msg = JSON.stringify(lastError.message);
        } else if (lastError.error && typeof lastError.error === 'string') {
          msg = lastError.error;
        } else if (lastError.error && typeof lastError.error === 'object' && typeof lastError.error.message === 'string') {
          msg = lastError.error.message;
        } else {
          msg = JSON.stringify(lastError);
        }
      }
      const lowerMsg = msg.toLowerCase();
      const prov = providersToTry[0] || 'AI';
      
      let helpfulMessage = `[${prov}] ${msg}`;
      
      if (lowerMsg.includes('429') || lowerMsg.includes('rate limit')) {
        helpfulMessage = `${prov} rate limit reached. Try again in 30 seconds or switch to another model/provider in AI Settings.`;
      } else if (lowerMsg.includes('401') || lowerMsg.includes('invalid') || lowerMsg.includes('key')) {
        helpfulMessage = `Invalid API key for ${prov}. Please check your ${prov} key in AI Settings.`;
      } else if (lowerMsg.includes('quota') || lowerMsg.includes('exhausted') || lowerMsg.includes('balance') || lowerMsg.includes('credit')) {
        helpfulMessage = `${prov} quota exceeded. Please check your ${prov} billing or use a different provider.`;
      } else if (lowerMsg.includes('network') || lowerMsg.includes('connection') || lowerMsg.includes('fetch')) {
        helpfulMessage = `Network error connecting to ${prov}. Please check your internet or ${prov} service status.`;
      } else if (lowerMsg.includes('timeout')) {
        helpfulMessage = `${prov} request timed out. The model might be overloaded. Try a lighter model.`;
      }

      const isRateLimit = lowerMsg.includes('429') || lowerMsg.includes('rate limit') || lowerMsg.includes('quota') || lowerMsg.includes('exhausted');
      
      console.error(`[API Router] Final failure: ${helpfulMessage}`);
      if (!context.silent) toast.error(helpfulMessage, { duration: 6000 });
      return {
        response: `Error: ${helpfulMessage}`,
        intent: isRateLimit ? 'rate_limit' : 'error',
        model: 'error',
        provider: prov,
        data: { retryAfter: 60 }
      };
    }
    
    return {
        response: "I encountered an unexpected error processing your request.",
        model: 'error',
        provider: providersToTry[0] || 'AI'
    };
  } catch (error: any) {
    console.error(`[API Router] Top-level Error:`, error);
    const msg = error.message || 'Unknown error';
    if (!context.silent) toast.error(`AI System Error: ${msg}`);
    return {
        response: `AI System Error: ${msg}`,
        model: 'error',
        provider: 'system'
    };
  }
}

